import { trackerTabSchema } from '@pickpro/contracts';
import type { ComponentType } from 'react';
import { useParams, type RouteObject } from 'react-router';
import { LegalPage } from '../features/legal/legal-page';
import { recoverFromChunkError } from './chunk-reload';
import { RouteErrorElement } from './route-error-element';
import { PublicOnly, RequireAdmin, RequireSession } from './guards';
import { LandingRoute } from './landing-route';
import { LocaleLayout } from './locale-layout';
import { NotFoundPage } from './not-found-page';
import { RouteHydrationFallback } from './route-hydration-fallback';
import { RootRedirect } from './root-redirect';
import { AdminLayout } from '../features/admin/admin-layout';

export const lazyPage =
  (load: () => Promise<Record<string, unknown>>, name: string) => async () => {
    try {
      const module = await load();
      return { Component: module[name] as ComponentType };
    } catch (error) {
      recoverFromChunkError(error);
      throw error;
    }
  };

const BET_VIEWS: readonly string[] = [...trackerTabSchema.options, 'performance'];
const RADAR_VIEWS: readonly string[] = ['matches', 'players'];

const lazyViewPage =
  (
    load: () => Promise<Record<string, unknown>>,
    name: string,
    param: string,
    views: readonly string[],
  ) =>
  async () => {
    const { Component } = await lazyPage(load, name)();
    const KnownView = () => {
      const view = useParams()[param];
      return view === undefined || views.includes(view) ? <Component /> : <NotFoundPage />;
    };
    return { Component: KnownView };
  };

export const routes: RouteObject[] = [
  { path: '/', element: <RootRedirect /> },
  {
    path: '/:locale',
    element: <LocaleLayout />,
    hydrateFallbackElement: <RouteHydrationFallback />,
    errorElement: <RouteErrorElement />,
    children: [
      { index: true, element: <LandingRoute /> },
      { path: 'terms', element: <LegalPage document="terms" /> },
      { path: 'privacy', element: <LegalPage document="privacy" /> },
      { path: 'responsible-gambling', element: <LegalPage document="responsible" /> },
      {
        element: <PublicOnly />,
        children: [
          {
            path: 'sign-up',
            lazy: lazyPage(() => import('../features/auth/sign-up-page'), 'SignUpPage'),
          },
          {
            path: 'sign-in',
            lazy: lazyPage(() => import('../features/auth/sign-in-page'), 'SignInPage'),
          },
          {
            path: 'forgot-password',
            lazy: lazyPage(
              () => import('../features/auth/forgot-password-page'),
              'ForgotPasswordPage',
            ),
          },
          {
            path: 'reset-password',
            lazy: lazyPage(
              () => import('../features/auth/reset-password-page'),
              'ResetPasswordPage',
            ),
          },
        ],
      },
      {
        path: 'verify-email',
        lazy: lazyPage(() => import('../features/auth/verify-email-page'), 'VerifyEmailPage'),
      },
      {
        element: <RequireSession />,
        children: [
          {
            path: ':sport',
            lazy: lazyPage(() => import('../features/today/today-page'), 'TodayPage'),
          },
          {
            path: ':sport/ticket-generator',
            lazy: lazyPage(
              () => import('../features/ticket-generator/ticket-generator-page'),
              'TicketGeneratorPage',
            ),
          },
          {
            path: ':sport/radar/:tab?',
            lazy: lazyViewPage(
              () => import('../features/radar/radar-page'),
              'RadarPage',
              'tab',
              RADAR_VIEWS,
            ),
          },
          {
            path: ':sport/search',
            lazy: lazyPage(() => import('../features/search/search-page'), 'SearchPage'),
          },
          {
            path: ':sport/competitions',
            lazy: lazyPage(
              () => import('../features/competitions/competitions-page'),
              'CompetitionsPage',
            ),
          },
          {
            path: ':sport/competitions/:id',
            lazy: lazyPage(
              () => import('../features/competitions/competition-page'),
              'CompetitionPage',
            ),
          },
          {
            path: ':sport/teams',
            lazy: lazyPage(() => import('../features/teams/teams-page'), 'TeamsPage'),
          },
          {
            path: ':sport/teams/:id',
            lazy: lazyPage(() => import('../features/teams/team-page'), 'TeamPage'),
          },
          {
            path: ':sport/players',
            lazy: lazyPage(() => import('../features/players/players-page'), 'PlayersPage'),
          },
          {
            path: ':sport/players/:id',
            lazy: lazyPage(() => import('../features/players/player-page'), 'PlayerPage'),
          },
          {
            path: ':sport/matches',
            lazy: lazyPage(() => import('../features/matches/matches-page'), 'MatchesPage'),
          },
          {
            path: ':sport/matches/:id/:section?/:group?',
            lazy: lazyPage(() => import('../features/matches/match-page'), 'MatchPage'),
          },
          {
            path: 'access',
            lazy: lazyPage(() => import('../features/access/access-page'), 'AccessPage'),
          },
          {
            path: 'settings',
            lazy: lazyPage(() => import('../features/settings/settings-page'), 'SettingsPage'),
          },
          {
            path: 'notebook',
            lazy: lazyPage(
              () => import('../features/ticket-generator/notebook-page'),
              'NotebookPage',
            ),
          },
          {
            path: 'bankrolls/:id?',
            lazy: lazyPage(() => import('../features/portfolio/bankroll-page'), 'BankrollPage'),
          },
          {
            path: 'bets/:tab?',
            lazy: lazyViewPage(
              () => import('../features/bets/bets-page'),
              'BetsPage',
              'tab',
              BET_VIEWS,
            ),
          },
        ],
      },
      {
        element: <RequireAdmin />,
        children: [
          {
            path: 'admin',
            element: <AdminLayout />,
            children: [
              {
                index: true,
                lazy: lazyPage(
                  () => import('../features/admin/admin-overview-page'),
                  'AdminOverviewPage',
                ),
              },
              {
                path: 'ingestion',
                lazy: lazyPage(
                  () => import('../features/admin/admin-ingestion-page'),
                  'AdminIngestionPage',
                ),
              },
              {
                path: 'users',
                lazy: lazyPage(
                  () => import('../features/admin/admin-users-page'),
                  'AdminUsersPage',
                ),
              },
              {
                path: 'users/:id',
                lazy: lazyPage(
                  () => import('../features/admin/admin-user-details-page'),
                  'AdminUserDetailsPage',
                ),
              },
            ],
          },
        ],
      },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
  { path: '*', element: <RootRedirect /> },
];
